import {
    WHITEBOARD_ADD_CONTACT,
    WHITEBOARD_ADD_PERMISSION,
    WHITEBOARD_CHANGE_PERMISSION,
    WHITEBOARD_COMPONENT_DRAGGING_STATE,
    WHITEBOARD_COMPONENT_RESIZE_STATE,
    WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE,
    WHITEBOARD_EXPORT_PDF_SUBMIT,
    WHITEBOARD_PERMISSIONS_FETCH,
    WHITEBOARD_PERMISSIONS_FETCH_COMPLETED,
    WHITEBOARD_PERMISSIONS_FETCH_FAILURE,
    WHITEBOARD_PROPERTY_CHANGE,
    WHITEBOARD_REMOVE_CONTACT,
    WHITEBOARD_REMOVE_PERMISSION,
    WHITEBOARD_REMOVE_PERMISSION_COMPLETED,
    WHITEBOARD_RESET,
    WHITEBOARD_SCALE,
    WHITEBOARD_SEARCH_CHANGE,
    WHITEBOARD_TOGGLE_ENFORCE_ASPECT_RATIO,
    WHITEBOARD_TOGGLE_GRID,
    WHITEBOARD_TOGGLE_SHARE_MODE,
} from "constants/action-types";
import { mutateAsync, requestAsync } from "lib/api";
import { deleteBoardPermission, getBoardPermissions } from "queries/whiteboard";
import { getContactsForWhiteboard } from "queries/contacts";
import { PAGE_LIMIT } from "constants/whiteboard";
import { List } from "immutable";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { EventTypes } from "redux-segment";
import me from "models/me";

export const reset = () => ({
    type: WHITEBOARD_RESET,
});

export const updateComponentResizeState = (isResizing: boolean) => ({
    payload: isResizing,
    type: WHITEBOARD_COMPONENT_RESIZE_STATE,
});

export const updateComponentDraggingState = (isDragging: boolean) => ({
    payload: isDragging,
    type: WHITEBOARD_COMPONENT_DRAGGING_STATE,
});

export const setScale = (scale: number) => ({
    meta: {
        analytics: EventTypes.track,
    },
    payload: scale,
    type: WHITEBOARD_SCALE,
});

export const toggleGrid = (value: boolean) => ({
    payload: value,
    type: WHITEBOARD_TOGGLE_GRID,
});

export const toggleSharePanel = (value?: boolean) => ({
    meta: {
        analytics: EventTypes.track,
    },
    payload: value,
    type: WHITEBOARD_TOGGLE_SHARE_MODE,
});

export const toggleEnforceAspectRatio = (value?: boolean) => ({
    payload: value,
    type: WHITEBOARD_TOGGLE_ENFORCE_ASPECT_RATIO,
});

export const changeSearchTerm = (value: string) => ({
    payload: value,
    type: WHITEBOARD_SEARCH_CHANGE,
});

export const updateBoardProperty = (key: string, value: any) => ({
    payload: {
        key,
        value,
    },
    type: WHITEBOARD_PROPERTY_CHANGE,
});

export const getPermissions = (boardId, minIdentity, userId) => {
    return (dispatch, getState) => {
        dispatch({
            payload: {
                boardId,
                minIdentity,
                userId,
            },
            type: WHITEBOARD_PERMISSIONS_FETCH,
        });
        dispatch(requestAsync(getBoardPermissions(boardId, minIdentity))).then((data) => {
            const { status, transformed } = data;
            if (status === 200) {
                const board = transformed.boardPermission[boardId];
                const users = transformed.boardPermission[boardId].users.toJS().map((user) => {
                    return user.identity;
                });
                const query = {
                    filters: {
                        "__buyers.__ref": users,
                    },
                };
                dispatch(requestAsync(getContactsForWhiteboard(query, boardId))).then((response) => {
                    if (response.status >= 200 && response.status <= 300) {
                        const companies = response.transformed;
                        const buyers = [];
                        const boardPermissionEntities = getState().entities.boardPermission;
                        const brandUsers = getState().scope.brand.modules.base.user_connections;
                        // Email and name are not stored on the boardPermission, we have to add those depending of if the identity (user_id)
                        // is a buyer or a brand user
                        updateBuyerPermissionsWithUserInfo({
                            companies,
                            users,
                            buyers,
                            board,
                            boardPermissionEntities,
                            boardId,
                        });
                        updateBuyerPermissionsWithBrandUserInfo({
                            board,
                            brandUsers,
                            boardPermissionEntities,
                            boardId,
                        });
                        filterPermissionsMissingData({ board, boardPermissionEntities, boardId, userId });
                        dispatch({
                            meta: {
                                hasMore: data.body.data.length === PAGE_LIMIT,
                                minIdentity: data.body.data[data.body.data.length - 1].identity,
                            },
                            payload: transformed._result,
                            type: WHITEBOARD_PERMISSIONS_FETCH_COMPLETED,
                        });
                    } else if (response.status === 401) {
                        me.default.logout(true);
                    } else {
                        let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
                        if (response.status === 403) {
                            errorMessage = formatMessage(
                                "You do not have permission to delete users on this whiteboard",
                            );
                        }

                        dispatch({
                            error: true,
                            meta: {
                                hasMore: false,
                            },
                            type: WHITEBOARD_PERMISSIONS_FETCH_COMPLETED,
                        });
                        dispatch(snackbarAdd({ message: errorMessage }));
                    }
                });
            } else if (status >= 400 && status < 500) {
                dispatch({
                    type: WHITEBOARD_PERMISSIONS_FETCH_FAILURE,
                });
            } else {
                dispatch({
                    type: WHITEBOARD_PERMISSIONS_FETCH_FAILURE,
                });
            }
        });
    };
};

const updateBuyerPermissionsWithUserInfo = ({ companies, users, buyers, board, boardPermissionEntities, boardId }) => {
    companies.forEach((company) => {
        company.__buyers.forEach((buyer) => {
            if (users.includes(buyer.__ref)) {
                buyers.push(buyer);
            }
        });
    });

    // Possible that this list contains a mix of brand users and buyers
    const boardUsers = board.users.toJS();
    buyers.forEach((buyer) => {
        const boardPermission = boardUsers.find((user) => {
            return user.identity === buyer.__ref;
        });
        boardPermission.name = buyer.__name;
        boardPermission.email = buyer.__email;
    });
    boardPermissionEntities[boardId] = boardPermissionEntities[boardId].update("users", (permissions) => {
        return List(boardUsers);
    });
};

const updateBuyerPermissionsWithBrandUserInfo = ({ board, brandUsers, boardPermissionEntities, boardId }) => {
    const boardUsers = board.users.toJS();
    boardUsers.forEach((boardUser) => {
        const matchingBrandUser = brandUsers.find((brandUser) => {
            return brandUser.ref === boardUser.identity;
        });

        if (matchingBrandUser) {
            boardUser.name = matchingBrandUser.name;
            boardUser.email = matchingBrandUser.email;
        }
    });

    boardPermissionEntities[boardId] = boardPermissionEntities[boardId].update("users", (users) => {
        return List(boardUsers);
    });
};

const filterPermissionsMissingData = ({ board, boardPermissionEntities, boardId, userId }) => {
    let boardUsers = board.users.toJS();
    boardUsers = boardUsers.filter((user) => {
        return user.email || user.name || user.identity === userId;
    });

    boardPermissionEntities[boardId] = boardPermissionEntities[boardId].update("users", (permissions) => {
        return List(boardUsers);
    });
};

export const addPermission = ({ boardId, userIds, userType, permission }) => {
    return {
        meta: {
            analytics: EventTypes.track,
        },
        payload: {
            boardId,
            permission,
            userIds,
            userType,
        },
        type: WHITEBOARD_ADD_PERMISSION,
    };
};

export const updatePermission = ({ board_id, identity, identity_type, role }) => {
    return {
        meta: {
            analytics: EventTypes.track,
        },
        payload: {
            board_id,
            identity,
            identity_type,
            role,
        },
        type: WHITEBOARD_CHANGE_PERMISSION,
    };
};

export const removePermission = ({ board_id, identity, identity_type, role }) => {
    return (dispatch, getState) => {
        dispatch({
            meta: {
                analytics: EventTypes.track,
            },
            type: WHITEBOARD_REMOVE_PERMISSION,
        });
        dispatch(mutateAsync(deleteBoardPermission({ board_id, identity, identity_type, role }))).then((data) => {
            const { status, transformed } = data;
            if (status >= 200 && status <= 300) {
                dispatch({
                    payload: transformed._result,
                    type: WHITEBOARD_REMOVE_PERMISSION_COMPLETED,
                });
            } else {
                let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
                if (status === 403) {
                    errorMessage = formatMessage("You do not have permission to delete users on this whiteboard");
                }
                dispatch({
                    error: true,
                    payload: new Error(errorMessage),
                    type: WHITEBOARD_REMOVE_PERMISSION_COMPLETED,
                });
                dispatch(snackbarAdd({ message: errorMessage }));
            }
        });
    };
};

export const addContact = (contact) => {
    return {
        meta: {
            analytics: EventTypes.track,
        },
        payload: contact,
        type: WHITEBOARD_ADD_CONTACT,
    };
};

export const removeContact = (contact) => {
    return {
        meta: {
            analytics: EventTypes.track,
        },
        payload: contact,
        type: WHITEBOARD_REMOVE_CONTACT,
    };
};

export const exportAsPDF = (boardId) => {
    return {
        meta: {
            analytics: EventTypes.track,
        },
        payload: {
            boardId,
        },
        type: WHITEBOARD_EXPORT_PDF_SUBMIT,
    };
};

export const acknowledgePDF = () => {
    return {
        type: WHITEBOARD_EXPORT_PDF_ACKNOWLEDGE,
    };
};
